import { type UseInfiniteQueryResult, useInfiniteQuery } from "react-query"
import { useParams } from "react-router-dom"

import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import type { Label } from "../../../types/generated/api/Label"
import type { PaginatedLabelList } from "../../../types/generated/api/PaginatedLabelList"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"
import type { UseLabelsOptions } from "../interfaces"

type ParamsType = {
  id: string
}

/**
 * Infinite labels loader with initial "Not provided" label and client-side filtering.
 */
export function useLabels({
  labelsSearchValue,
  notProvidedLabelName,
  onError,
}: UseLabelsOptions): UseInfiniteQueryResult<Label, AxiosError> {
  const { id: projectId } = useParams<ParamsType>()
  return useInfiniteQuery<AxiosResponse<PaginatedLabelList>, AxiosError, Label>(
    ["labels", labelsSearchValue, projectId],
    ({ pageParam = 1 }) =>
      KonanAPI.RetrieveLabels({
        project_uuid: projectId as string,
        page: pageParam,
        pageSize: 10,
        search: labelsSearchValue,
      }),
    {
      select: (data) => ({
        pageParams: data.pageParams,
        pages: data.pages
          .reduce<Label[]>(
            (acc, page) => (page.data.results ? [...acc, ...page.data.results] : acc),
            [
              {
                uuid: "",
                name: notProvidedLabelName,
                created_at: "",
                created_by: "",
              },
            ],
          )
          .filter((item) => (labelsSearchValue ? item.name.includes(labelsSearchValue) : true)),
      }),
      keepPreviousData: true,
      getNextPageParam: (lastPage) =>
        lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false,
      onError,
    },
  )
}
